<template>
	<div
		class="offer"
		v-if="offer"
		:key="offer.name"
		:class="[
			offer.attributes.flag &&
				`offer--${spaceToKebabCase(offer.attributes.flag)}`,
			{
				'offer--promo-highlighted':
					offer.attributes.promo_bonus_percent &&
					offer.attributes.promo_bonus_percent > 50,
			},
		]"
	>
		<div class="offer__content">
			<div
				v-if="offer.attributes.flag"
				class="offer__content-header"
				:class="`offer__content-header--${spaceToKebabCase(offer.attributes.flag)}`"
			>
				<BoxHeaderBackground
					:slug="`offer-shop-item-header-${offer.name}`"
				/>
				<div class="offer__content-header-text-container">
					<span>
						{{
							translate(
								`ui.${getEnumKeyByValue(offer.attributes.flag)?.toLowerCase()}.non_translatable`,
							)
						}}
					</span>
				</div>
			</div>
			<div
				class="offer__image"
				:class="`offer-reward--${offer.attributes.skin}-120`"
			/>
			<div class="offer__values">
				<AppPrice
					v-for="(item, index) in offer.attributes?.slots?.[0]
						?.items ?? ([] as ResourceOrUtilitySlotItem[])"
					:key="index"
					:amount="item.qty"
					:currency="(item as ResourceOrUtilitySlotItem).key"
					:resourceCheck="false"
					:resourceChangeSign="ResourceChangeHighlight.POSITIVE"
					:fontSize="26"
					:iconSize="24"
					:fontWeight="FontWeights.BOLD"
					:allowShrink="true"
				/>
			</div>
			<AppTextButton
				:class="{ 'dual-offer-price': dualPrice[1] }"
				:type="StatusTypes.SECONDARY"
				:text="formatPrice(dualPrice[0].value, dualPrice[0].currency)"
				:fontSize="20"
				:fontWeight="FontWeights.BOLD"
				@click="buyPackage(offer)"
			>
				<template #content v-if="dualPrice[1]">
					<span class="dual-price"
						>({{
							formatPrice(
								dualPrice[1].value,
								dualPrice[1].currency,
							)
						}})</span
					>
				</template>
			</AppTextButton>
		</div>
		<RectangleBoxContainer />
		<div v-if="offer.attributes.promo_bonus_percent" class="offer__bonus">
			<div
				class="fabric-ribbon-left"
				:class="`fabric-ribbon-left--${(offer.attributes.promo_bonus_percent > 50 ? true : false) ? 'warning' : 'primary'}-86`"
			>
				<span>{{
					"+" + offer.attributes.promo_bonus_percent + " %"
				}}</span>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import BoxHeaderBackground from "@/components/SVG/BoxHeaderBackground.vue";
import RectangleBoxContainer from "@/components/SVG/RectangleBoxContainer.vue";
import { OfferAnalyticsClickSource } from "@/libs/SocketService";
import { PaymentProvider } from "@/services/PaymentProvider";
import {
	OfferFlagLabels,
	type ResourceOrUtilitySlotItem,
	type ShopItem,
} from "@/types/shop";
import { FontWeights, ResourceChangeHighlight, StatusTypes } from "@/types/ui";
import { formatPrice } from "@/utils/format";
import type { DualPrice } from "@/utils/price";
import { getDualPrice } from "@/utils/price";
import { spaceToKebabCase } from "@/utils/string";
import { translate } from "@/utils/translate";
import { computed } from "vue";

interface Props {
	offer: ShopItem;
}
const { offer } = defineProps<Props>();

const dualPrice = computed((): DualPrice => {
	return getDualPrice(offer?.currency || "", offer?.retail_price || 0);
});

const buyPackage = async (offer: ShopItem): Promise<void> => {
	PaymentProvider.buyPackage(offer.store_id, {
		clickSource: OfferAnalyticsClickSource.STORE,
	});
};

const getEnumKeyByValue = (value: string): string | undefined => {
	return Object.keys(OfferFlagLabels).find(
		(key: string): boolean =>
			OfferFlagLabels[key as keyof typeof OfferFlagLabels] === value,
	);
};
</script>

<style lang="scss" scoped>
.offer {
	position: relative;
	box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
	background: linear-gradient(0deg, rgb(92, 4, 20) 0%, rgb(195, 64, 64) 100%);
	width: calc(50% - 0.5rem);
	height: 14.5rem;

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		mix-blend-mode: color-burn;
		@include background(
			url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
			150%,
			right top,
			repeat
		);
		width: 100%;
		height: 100%;
		content: "";
	}

	&__values {
		margin-top: -1.5rem;
		padding: 0 0.5rem;
		width: 100%;

		&--black-friday {
			:deep(.price) {
				.price__value {
					color: #fff3dd;
				}

				.price__bonus {
					color: #f4d278;
				}
			}
		}
	}

	&__content {
		position: absolute;
		@include flex-col-start;
		justify-content: space-between;
		gap: 0.5rem;
		z-index: 1;
		padding: 2rem 0 0.75rem;
		width: 100%;
		height: 100%;

		&-header {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 1.563rem;
			// NOTE: z-index cannot be here

			:deep(.box-header-bg) {
				position: relative;
				mix-blend-mode: multiply;
				width: 100%;
				height: 1.563rem;
			}

			&--most-popular {
				:deep(.box-header-bg) {
					.solid-bg-polygon {
						fill: rgba(3, 81, 123, 0.8) !important;
					}

					.texture-bg-polygon {
						opacity: 0.2 !important;
					}
				}
			}

			&--best-value {
				:deep(.box-header-bg) {
					.solid-bg-polygon {
						fill: rgba(1, 127, 52, 0.8) !important;
					}

					.texture-bg-polygon {
						opacity: 0.2 !important;
					}
				}
			}

			&-text-container {
				@include flex-row-center;
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;

				span {
					padding-bottom: 0.25rem;
					color: #f2dcb5;
					font-weight: 800;
					font-size: 0.875rem;
					text-align: center;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
					text-transform: uppercase;
				}
			}
		}

		.text-button {
			width: calc(100% - 2.25rem);
		}

		.dual-offer-price {
			:deep(.text-button) {
				flex-direction: column;

				.text-button__text-container {
					padding-bottom: 0;
					size: 1rem;
					.shrink-text {
						font-size: 1rem;
						line-height: 1 !important;
					}
				}
				.dual-price {
					opacity: 0.9;
					font-size: 0.625rem;
				}
			}
		}
	}

	&__image {
		z-index: 2;
	}

	:deep(.rectangle-box-container) {
		.rectangle-box,
		.rectangle-box-decoration {
			z-index: 1;
		}
	}

	&__bonus {
		position: absolute;
		top: 50%;
		left: -0.6rem;
		transform: translateY(-50%);
		z-index: 1;

		.fabric-ribbon-left {
			@include flex-col-center;
			padding-top: 0.25rem;
			padding-right: 0.75rem;
			padding-bottom: 0.625rem;
			padding-left: 0.25rem;

			span {
				padding-bottom: 0.375rem;
				color: #fff3dd;
				font-weight: 800;
				font-size: 1.375rem;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
		}
	}
}
</style>
